/**
 * Pure display helpers for World Cup groups list (ordinal rank, card emoji, pot estimate).
 */

export const WORLD_CUP_GROUP_CARD_EMOJIS = [
  '⚽',
  '🏆',
  '🥅',
  '🇧🇷',
  '🇦🇷',
  '🇪🇸',
  '🇫🇷',
  '🇵🇹',
  '🇩🇪',
  '🇮🇹',
] as const;

export function getWorldCupGroupCardEmoji(groupId: string): string {
  let h = 0;
  for (let i = 0; i < groupId.length; i += 1) {
    h = (Math.imul(31, h) + groupId.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(h) % WORLD_CUP_GROUP_CARD_EMOJIS.length;
  return WORLD_CUP_GROUP_CARD_EMOJIS[idx] ?? '⚽';
}

export function formatWorldCupOrdinalRank(rank: number): string {
  if (!Number.isFinite(rank) || rank < 1) return '—';
  const n = Math.floor(rank);
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return `${n}st`;
  if (j === 2 && k !== 12) return `${n}nd`;
  if (j === 3 && k !== 13) return `${n}rd`;
  return `${n}th`;
}

/** Current pool if every active member pays the entry fee (integer coins). */
export function getEstimatedGroupPotCoins(entryFeeCoins: number, memberCount: number): number {
  const fee = Math.max(0, Math.floor(entryFeeCoins));
  const members = Math.max(0, Math.floor(memberCount));
  return fee * members;
}

/**
 * Returns per-place coin amounts for the prize pool display.
 * WINNER_TAKE_ALL → single entry for 1st place.
 * TOP_THREE → three entries using the stored percentages (defaults: 50/30/20).
 */
export function getGroupCoinPayoutBreakdown(
  entryFeeCoins: number,
  memberCount: number,
  payoutMode: string | null | undefined,
  payoutFirstPct: number | null | undefined,
  payoutSecondPct: number | null | undefined,
  payoutThirdPct: number | null | undefined,
): Array<{ place: number; coins: number }> {
  const pot = getEstimatedGroupPotCoins(entryFeeCoins, memberCount);
  if (pot <= 0) return [];
  if (payoutMode === 'TOP_THREE') {
    const first = payoutFirstPct ?? 50;
    const second = payoutSecondPct ?? 30;
    const third = payoutThirdPct ?? 20;
    return [
      { place: 1, coins: Math.floor((pot * first) / 100) },
      { place: 2, coins: Math.floor((pot * second) / 100) },
      { place: 3, coins: Math.floor((pot * third) / 100) },
    ];
  }
  return [{ place: 1, coins: pot }];
}

/** Display rank like `#04` or `#152` (Podium-style). */
export function formatWorldCupRankBadge(rank: number): string {
  if (!Number.isFinite(rank) || rank < 1) return '#—';
  const n = Math.floor(rank);
  if (n >= 100) return `#${n}`;
  return `#${n.toString().padStart(2, '0')}`;
}

/** How many members to show as stacked placeholders before the "+N" chip. */
export const WORLD_CUP_GROUP_MEMBER_STACK_VISIBLE = 2 as const;

export function getWorldCupGroupMemberOverflowCount(memberCount: number): number {
  const n = Math.max(0, Math.floor(memberCount));
  return Math.max(0, n - WORLD_CUP_GROUP_MEMBER_STACK_VISIBLE);
}
